/**
 * /join — screen 3u. The front door.
 *
 * Vertically centred, no navigation, 26px gutters: the passcode, the human
 * check, Continue, and the login half for anyone who already has an account.
 *
 * Login takes one field, a username or an email, and the server decides what
 * happens (`/api/auth/lookup`). Three of the four outcomes say the same thing —
 * an unknown username, any email, and a confirmed username all answer "check
 * your inbox" — so the reply cannot be used to ask the app which usernames
 * exist (D7).
 *
 * The fourth is the exception and the point: a username that exists but has
 * never confirmed an address. Its owner has no way to receive a link, so they
 * are offered the address that fixes it, or the door. The door is labelled with
 * what it means, because an account anyone can walk into is a thing the person
 * choosing it should be told about rather than protected from.
 *
 * **Turnstile (D8).** The widget is an optional layer, not a hard dependency.
 * The script is fetched only after `GET /api/auth/turnstile-key` hands back a
 * site key; when that endpoint 404s or returns nothing — a dev machine, a
 * deployment without the secret — nothing is rendered, nothing is loaded, and
 * the form still works. The server is what decides whether a token was
 * required, and it already knows whether Turnstile is configured.
 *
 * The 429 path gets the D8 lockout copy rather than a bare error, because the
 * honest answer to "the group's passcode has been typed wrong ten times in two
 * minutes" is "wait a moment," not "invalid credentials." How long is read off
 * the server's `retry-after` rather than written into the sentence.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useStore } from '../lib/store'
import { ApiFailure, GET, POST } from '../lib/api'
import { Button, Field, Notice, Screen, Spacer } from '../ui'
import { Wordmark } from '../ui/Wordmark'
import { AdSlot } from '../ui/AdSlot'

const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

interface TurnstileRenderOptions {
  sitekey: string
  theme?: 'auto' | 'light' | 'dark'
  callback?: (token: string) => void
  'error-callback'?: () => void
  'expired-callback'?: () => void
}

interface TurnstileApi {
  render: (el: HTMLElement, opts: TurnstileRenderOptions) => string | undefined
  reset: (widgetId?: string) => void
  remove: (widgetId?: string) => void
}

declare global {
  interface Window { turnstile?: TurnstileApi }
}

interface TurnstileKeyResponse { siteKey?: string | null; sitekey?: string | null }

let scriptPromise: Promise<void> | null = null

/** Load the Turnstile script exactly once, and only when there is a key. */
function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve()
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise<void>((resolve, reject) => {
    const el = document.createElement('script')
    el.src = TURNSTILE_SRC
    el.async = true
    el.defer = true
    el.onload = () => resolve()
    el.onerror = () => { scriptPromise = null; reject(new Error('turnstile script blocked')) }
    document.head.appendChild(el)
  })
  return scriptPromise
}

type Stage =
  | { kind: 'ask' }
  /** Same words for an unknown name, any email, and a confirmed name (D7). */
  | { kind: 'sent' }
  /** The one branch that says something specific, and the reason it exists. */
  | { kind: 'unconfirmed'; displayName: string; hasEmail: boolean }

const CHECK_INBOX =
  'Check your inbox for a login link. Look in your spam folder too, it likes to hide there.'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * The wait, from the server's own `retry-after`, rather than a number written
 * into the copy. The hardcoded version said ten minutes, the limit said
 * fifteen, and both were wrong the moment the limit was retuned.
 */
function lockoutCopy(retryAfterSeconds?: number): string {
  if (!retryAfterSeconds) return 'Too many tries. Take a breath and try again shortly.'
  if (retryAfterSeconds <= 90) {
    const s = Math.max(10, Math.ceil(retryAfterSeconds / 10) * 10)
    return `Too many tries. Take a breath and try again in about ${s} seconds.`
  }
  const mins = Math.ceil(retryAfterSeconds / 60)
  return `Too many tries. Take a breath and try again in ${mins} minute${mins === 1 ? '' : 's'}.`
}

export default function Join() {
  const navigate = useNavigate()
  const { refresh } = useStore()

  const [passcode, setPasscode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lockout, setLockout] = useState<{ retryAfter?: number } | null>(null)

  const [siteKey, setSiteKey] = useState<string | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const widgetHost = useRef<HTMLDivElement | null>(null)
  const widgetId = useRef<string | undefined>(undefined)

  // The login half of this screen. `stage` is what the identifier lookup came
  // back with, and it is the only thing that decides what is on screen.
  // Opening straight on the login half is what the setup screen's Login button
  // wants; read once, from the URL, so a refresh keeps you where you were.
  const [signinOpen, setSigninOpen] = useState(
    () => new URLSearchParams(window.location.search).get('signin') === '1',
  )
  const [identifier, setIdentifier] = useState('')
  const [signinBusy, setSigninBusy] = useState(false)
  const [stage, setStage] = useState<Stage>({ kind: 'ask' })
  const [confirmEmail, setConfirmEmail] = useState('')

  // ── is there a site key at all? ───────────────────────────────────────────

  useEffect(() => {
    const ac = new AbortController()
    void (async () => {
      try {
        const res = await GET<TurnstileKeyResponse>('/api/auth/turnstile-key', ac.signal)
        const key = res.siteKey ?? res.sitekey ?? null
        if (!ac.signal.aborted && key) setSiteKey(key)
      } catch {
        // No endpoint, or no key configured. Turnstile simply isn't part of this
        // deployment; the passcode gate and the DO rate limiter still are.
      }
    })()
    return () => ac.abort()
  }, [])

  // ── render the widget once the key exists ─────────────────────────────────

  useEffect(() => {
    if (!siteKey) return
    let cancelled = false
    void (async () => {
      try {
        await loadTurnstile()
        if (cancelled || !widgetHost.current || !window.turnstile) return
        widgetId.current = window.turnstile.render(widgetHost.current, {
          sitekey: siteKey,
          theme: 'dark',
          callback: (t: string) => setToken(t),
          'error-callback': () => setToken(null),
          'expired-callback': () => setToken(null),
        })
      } catch {
        // Blocked by an extension or offline. Proceed without it.
        if (!cancelled) setSiteKey(null)
      }
    })()
    return () => {
      cancelled = true
      if (widgetId.current && window.turnstile) {
        try { window.turnstile.remove(widgetId.current) } catch { /* already gone */ }
        widgetId.current = undefined
      }
    }
  }, [siteKey])

  const resetTurnstile = useCallback(() => {
    setToken(null)
    if (widgetId.current && window.turnstile) {
      try { window.turnstile.reset(widgetId.current) } catch { /* already gone */ }
    }
  }, [])

  // ── continue ──────────────────────────────────────────────────────────────

  const submit = useCallback(async () => {
    setBusy(true)
    setError(null)
    setLockout(null)
    try {
      const res = await POST<{ needsProfile: boolean }>('/api/auth/passcode', {
        passcode: passcode.trim(),
        ...(token ? { turnstileToken: token } : null),
      })
      if (res.needsProfile) navigate('/setup')
      else await refresh()
    } catch (e) {
      resetTurnstile()
      if (e instanceof ApiFailure && (e.code === 'rate_limited' || e.status === 429)) {
        setLockout({ retryAfter: e.retryAfter })
        setError(null)
      } else {
        setError(e instanceof ApiFailure ? e.message : 'Couldn’t get you in. Try again.')
      }
    } finally {
      setBusy(false)
    }
  }, [navigate, passcode, refresh, resetTurnstile, token])

  // ── "signed in on another device" (D7) ────────────────────────────────────

  const lookup = useCallback(async () => {
    setSigninBusy(true)
    setError(null)
    try {
      const res = await POST<
        { kind: 'link_sent' } | { kind: 'unconfirmed'; displayName: string; hasEmail: boolean }
      >('/api/auth/lookup', { identifier: identifier.trim() })
      setStage(res.kind === 'unconfirmed'
        ? { kind: 'unconfirmed', displayName: res.displayName, hasEmail: res.hasEmail }
        : { kind: 'sent' })
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : 'Couldn’t look that up.')
    } finally {
      setSigninBusy(false)
    }
  }, [identifier])

  const attachEmail = useCallback(async () => {
    setSigninBusy(true)
    setError(null)
    try {
      await POST('/api/auth/unconfirmed/attach', {
        name: identifier.trim(), email: confirmEmail.trim(),
      })
      setStage({ kind: 'sent' })
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : 'Couldn’t send that link.')
    } finally {
      setSigninBusy(false)
    }
  }, [confirmEmail, identifier])

  const enterUnconfirmed = useCallback(async () => {
    setSigninBusy(true)
    setError(null)
    try {
      await POST('/api/auth/unconfirmed/enter', { name: identifier.trim() })
      await refresh()
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : 'Couldn’t log you in.')
      setSigninBusy(false)
    }
  }, [identifier, refresh])

  return (
    <Screen style={{ justifyContent: 'center', padding: '0 26px' }}>
      <main style={{ width: '100%', maxWidth: 420, margin: '0 auto' }}>
        {/* The wordmark is the app's name set properly; an emoji and 26px of
            Archivo were standing in for it. */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Wordmark width={300} style={{ color: 'var(--fg)' }} />
        </div>

        {lockout ? (
          <div style={{ marginTop: 26 }}>
            <Notice tone="alert">{lockoutCopy(lockout.retryAfter)}</Notice>
          </div>
        ) : null}

        {!signinOpen ? (
          <>
            <div style={{ marginTop: lockout ? 16 : 34 }}>
              {/* No caption above the box: the placeholder is the instruction,
                  and a "Passcode" label over an "enter promo code" box reads as
                  two different things being asked for. */}
              <Field
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && passcode.trim()) void submit() }}
                autoComplete="one-time-code"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder="enter promo code"
                error={error}
                aria-label="Promo code"
              />
            </div>

            {/* Rendered only when a key came back; otherwise nothing is here. */}
            {siteKey ? (
              <div
                ref={widgetHost}
                style={{ marginTop: 12, display: 'flex', justifyContent: 'center', minHeight: 65 }}
              />
            ) : null}

            <Spacer h={12} />
            <Button onClick={() => { void submit() }} disabled={busy || !passcode.trim()}>
              {busy ? 'Checking…' : 'Continue'}
            </Button>

            <div style={{ marginTop: 26, textAlign: 'center', fontSize: 13, color: 'var(--dim)', lineHeight: 2 }}>
              Already have an account?
              <br />
              <button
                onClick={() => { setSigninOpen(true); setError(null); setStage({ kind: 'ask' }) }}
                style={{ color: 'var(--bright)', fontSize: 13, fontWeight: 600, minHeight: 44 }}
              >
                Log in with a username or email →
              </button>
            </div>
          </>
        ) : stage.kind === 'sent' ? (
          <>
            <div style={{ marginTop: 30, fontSize: 17, fontWeight: 700 }}>Check your email</div>
            <div style={{ fontSize: 14, color: 'var(--dim)', marginTop: 10, lineHeight: 1.55 }}>
              {CHECK_INBOX}
            </div>
            <Spacer h={22} />
            <Button
              variant="tertiary"
              onClick={() => { setStage({ kind: 'ask' }); setError(null) }}
            >
              Use a different username or email
            </Button>
          </>
        ) : stage.kind === 'unconfirmed' ? (
          <>
            <div style={{ marginTop: 30, fontSize: 17, fontWeight: 700 }}>
              Enter your email to confirm this username
            </div>
            <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 8, lineHeight: 1.5 }}>
              “{stage.displayName}” exists but has never confirmed an email.
            </div>
            <Spacer h={14} />
            <Field
              label="Email"
              type="email"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && EMAIL_RE.test(confirmEmail.trim())) void attachEmail()
              }}
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="email"
              placeholder="you@example.com"
              error={error}
              aria-label="Your email"
            />
            <Spacer h={14} />
            <Button
              onClick={() => { void attachEmail() }}
              disabled={signinBusy || !EMAIL_RE.test(confirmEmail.trim())}
            >
              {signinBusy ? 'Sending…' : 'Send my login link'}
            </Button>

            <div style={{ marginTop: 28, textAlign: 'center' }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>No thanks, just log me in</div>
              <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 6, lineHeight: 1.5 }}>
                Note: anyone can access this account until your email is confirmed.
              </div>
              <Spacer h={12} />
              <Button
                variant="secondary"
                onClick={() => { void enterUnconfirmed() }}
                disabled={signinBusy}
              >
                Log in as {stage.displayName}
              </Button>
            </div>

            <Spacer h={16} />
            <Button
              variant="tertiary"
              onClick={() => { setStage({ kind: 'ask' }); setError(null) }}
            >
              Back
            </Button>
          </>
        ) : (
          <>
            <div style={{ marginTop: 30, fontSize: 15, fontWeight: 700 }}>Log in</div>
            <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 6, lineHeight: 1.5 }}>
              This site uses email links rather than passwords. I don’t want your data. Entering
              your username or email address will send a login link to your email.
            </div>
            <Spacer h={14} />
            <Field
              label="Username or email"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && identifier.trim()) void lookup() }}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              error={error}
              aria-label="Username or email"
            />
            <Spacer h={14} />
            <Button onClick={() => { void lookup() }} disabled={signinBusy || !identifier.trim()}>
              {signinBusy ? 'Checking…' : 'Continue'}
            </Button>
            <Spacer h={8} />
            <Button variant="tertiary" onClick={() => { setSigninOpen(false); setError(null) }}>
              Back to the passcode
            </Button>
          </>
        )}

        {/* No legal links on the front door. Nothing has been agreed to yet and
            nothing is being collected; the acceptance that matters is on /setup,
            where the checkbox and these same two links are the gate (D20). Two
            links to unread documents under a passcode box is furniture. */}

        <AdSlot marginTop={30} />
      </main>
    </Screen>
  )
}
